// frontend/src/lib/copySlots.ts
// #1039 / AP-26. THE closed, named set of staff-editable family-facing strings.
//
// This file is the source of truth. A build-time generator emits
// `backend/src/data/copy/copy-slots.json` from it, because no runtime
// cross-stack import exists (the same shape the form seed already uses).
//
// HOW TO READ A SLOT. `seed` is the wording that ships in the bundle and the
// value a family sees until TRF edits it. The store holds OVERRIDES only, so a
// slot nobody has touched costs nothing and can never render empty. `screen`
// and `label` are what the staff editor groups and lists by — a file path is
// developer metadata, and asking a comms owner to tell `EligibilityCheck.tsx`
// from `InviteRequiredStop.tsx` (two near-identical stop screens) is asking her
// to guess.
//
// HOW TO ADD ONE. Lift the seed from the live component BYTE FOR BYTE — never
// retype it, and never copy it out of a doc. Every string here uses U+2019 for
// apostrophes and U+2014 for dashes; `docs/architecture/referral-only-intake.md:119`
// quotes one of these very strings with a straight `'`, and the PRD does the
// same to the pre-screen sentence. `copySlots.verify.test.ts` asserts each seed
// still appears verbatim in its source file, which is what catches a
// transcription slip on the day it happens rather than on the day a family
// reads it.

export type SlotKind = 'prose' | 'composed' | 'linked' | 'url' | 'list';

export interface ComposedValue {
  text: string;
  /** [start, end) of the emphasised span, or null. Offsets, never a substring —
   *  a second copy of the text desynchronises the moment someone edits one. */
  emphasis: [number, number] | null;
}

export interface LinkedValue {
  text: string;
  link: [number, number];
  href: string;
}

export interface CopySlot {
  kind: SlotKind;
  seed: string | ComposedValue | LinkedValue | Array<Record<string, unknown>>;
  /** The screen a family sees, in words a non-engineer recognises. Required. */
  screen: string;
  /** This string's role on that screen. Required. */
  label: string;
  group: string;
  order: number;
  audience: 'family' | 'staff';
  /** Where it renders. Developer metadata — never the thing staff navigate by. */
  surface: string;
  status?: 'active' | 'deprecated';
  /** A locked slot renders through copy() so the CI guard is satisfied, and is
   *  display-only in the editor: the wording is code-owned (DECISIONS 2026-06-02). */
  locked?: boolean;
  /** Advisory soft cap, shown as a live counter. Warns; never blocks. */
  max_chars?: number;
  /** Permits the NAME of a legal document (never a consent construction). */
  legal_link_ok?: boolean;
  note?: string;
}

const INVITE_STOP_SURFACE = 'components/apply/InviteRequiredStop.tsx';
const NO_CODE_SCREEN = 'No referral code';
const EXPIRED_SCREEN = 'Expired link';
const CLOSED_SCREEN = 'Closed cycle';
const NEW_FAMILIES_SCREEN = 'Renewing families only';
const TYPO_SCREEN = 'Mistyped code';

const RENEWAL_CARD_SCREEN = 'Grant renewal';

const STATUS_SURFACE = 'pages/apply/ApplicationStatusPage.tsx';
const STATUS_SCREEN = 'Application status';
const TIMELINE_SCREEN = 'Progress steps';
const NEEDS_INFO_SCREEN = 'Need more information';
const DECISION_SCREEN = 'Note from TRF';

export const COPY_SLOTS = {
  // ── shared chrome, both invite-stop variants ─────────────────────────────
  // Namespaced `invite_stop`, not `invite_required`: these render on BOTH
  // variants, and filing them under one would quietly make them variant-specific.
  'apply.invite_stop.document_label': {
    kind: 'prose',
    seed: 'Apply to TRF',
    screen: 'Shared wording',
    label: 'The name of the document',
    group: 'apply.invite_stop', order: 10, audience: 'family',
    surface: INVITE_STOP_SURFACE,
    locked: true,
    note: 'DECISIONS 2026-06-02 pins this wordmark to the pre-admission flow.',
  },
  'apply.invite_stop.eyebrow': {
    kind: 'prose',
    seed: '— Apply to TRF',
    screen: 'Shared wording',
    label: 'The short line above the headline',
    group: 'apply.invite_stop', order: 20, audience: 'family',
    surface: INVITE_STOP_SURFACE,
    locked: true,
    note: 'Locked because it CONTAINS the pinned wordmark. The leading em dash is '
      + 'the house MonoLabel convention, baked into the string rather than rendered.',
  },
  'apply.invite_stop.alternate_prompt': {
    kind: 'prose',
    seed: 'Already started?',
    screen: 'Shared wording',
    label: 'Offer to sign in if they already started',
    group: 'apply.invite_stop', order: 30, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 40,
  },
  'apply.invite_stop.alternate_link_label': {
    kind: 'prose',
    seed: 'Sign in',
    screen: 'Shared wording',
    label: 'The sign-in link’s words',
    group: 'apply.invite_stop', order: 40, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 24,
  },
  'apply.invite_stop.footer_attribution': {
    kind: 'prose',
    seed: 'A program of the Tyler Robinson Foundation',
    screen: 'Shared wording',
    label: 'The credit line at the foot',
    group: 'apply.invite_stop', order: 50, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 80,
  },
  // WITHDRAWN at wire-up (PR-C): there was an `apply.invite_stop.get_support_url`
  // slot here, `kind: 'url'`, labelled “Where ‘get in touch’ points”. Wiring the
  // consumer showed it had no consumer to wire — the only two anchors on these
  // screens live INSIDE sentences and are `linked` slots that carry their own
  // href, which is the whole reason `linked` exists (D-CS-13).
  //
  // Keeping it would have put a field in Melanie's editor that changes nothing:
  // she edits the destination, publishes, sees the old link, and stops trusting
  // the tool. Wiring the anchors to it INSTEAD would have made each `linked`
  // slot's own href a decoration, which is the same lie one layer down. Rev 5
  // already made this call once, on `ready_for_publish`: a control with no
  // reader is removed, not given one.
  //
  // The rot risk that motivated it (GH-620 — trf.org moved a page and the link
  // 404'd) is real and unchanged. It is now answered by the two `linked` slots
  // being editable, plus the contract assertion that anchors reading the same
  // words point at the same place, so a half-finished fix fails CI instead of
  // shipping one live link and one dead one.

  // ── the no-code variant ──────────────────────────────────────────────────
  'apply.invite_required.meta_how_to_apply_key': {
    kind: 'prose', seed: 'How to apply',
    screen: NO_CODE_SCREEN, label: '“How to apply” — the question',
    group: 'apply.invite_required', order: 100, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 24,
  },
  'apply.invite_required.meta_how_to_apply_value': {
    kind: 'prose', seed: 'By referral',
    screen: NO_CODE_SCREEN, label: '“How to apply” — the answer',
    group: 'apply.invite_required', order: 110, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 40,
  },
  'apply.invite_required.meta_who_can_refer_key': {
    kind: 'prose', seed: 'Who can refer',
    screen: NO_CODE_SCREEN, label: '“Who can refer” — the question',
    group: 'apply.invite_required', order: 120, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 24,
  },
  'apply.invite_required.meta_who_can_refer_value': {
    kind: 'prose', seed: 'Your hospital social worker or TRF',
    screen: NO_CODE_SCREEN, label: '“Who can refer” — the answer',
    group: 'apply.invite_required', order: 130, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 48,
  },
  'apply.invite_required.headline': {
    kind: 'composed',
    seed: { text: 'This application opens by referral.', emphasis: [23, 34] },
    screen: NO_CODE_SCREEN, label: 'Headline',
    group: 'apply.invite_required', order: 140, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 80,
  },
  'apply.invite_required.lede': {
    kind: 'prose',
    seed: 'To make sure every family gets the care and attention they deserve, TRF '
      + 'applications currently begin with a personal invitation. You don’t '
      + 'need to do anything complicated — the right link is one conversation away.',
    screen: NO_CODE_SCREEN, label: 'Opening paragraph',
    group: 'apply.invite_required', order: 150, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 320,
  },
  'apply.invite_required.point_social_worker': {
    kind: 'prose',
    seed: 'Ask your hospital social worker for a TRF application link or code',
    screen: NO_CODE_SCREEN, label: 'What to do next — ask the social worker',
    group: 'apply.invite_required', order: 160, audience: 'family', max_chars: 120,
    surface: INVITE_STOP_SURFACE,
  },
  'apply.invite_required.point_contact_trf': {
    kind: 'linked',
    seed: {
      text: 'Not sure who that is, or don’t have one yet? Reach the Tyler Robinson '
        + 'Foundation team directly at trf.org/get-support — they can point you to '
        + 'the right hospital contact and get the referral started',
      link: [98, 117],
      href: 'https://www.trf.org/get-support',
    },
    screen: NO_CODE_SCREEN, label: 'What to do next — get in touch with TRF',
    group: 'apply.invite_required', order: 170, audience: 'family', max_chars: 280,
    surface: INVITE_STOP_SURFACE,
    note: 'The link inside this sentence is why the slot is `linked` and not three '
      + 'fragments: GH-620’s fix was “every point that tells a family to go '
      + 'somewhere carries the place to go”, and fragments pin it to one position.',
  },
  'apply.invite_required.point_ready': {
    kind: 'prose',
    seed: 'Your link or code opens this same application — ready when you are',
    screen: NO_CODE_SCREEN, label: 'What to do next — the link opens this same application',
    group: 'apply.invite_required', order: 180, audience: 'family', max_chars: 120,
    surface: INVITE_STOP_SURFACE,
  },

  // ── the expired-link variant ─────────────────────────────────────────────
  'apply.invite_invalid.meta_this_link_key': {
    kind: 'prose', seed: 'This link',
    screen: EXPIRED_SCREEN, label: '“This link” — the question',
    group: 'apply.invite_invalid', order: 200, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 24,
  },
  'apply.invite_invalid.meta_this_link_value': {
    kind: 'prose', seed: 'No longer valid',
    screen: EXPIRED_SCREEN, label: '“This link” — the answer',
    group: 'apply.invite_invalid', order: 210, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 40,
  },
  'apply.invite_invalid.meta_what_to_do_key': {
    kind: 'prose', seed: 'What to do',
    screen: EXPIRED_SCREEN, label: '“What to do” — the question',
    group: 'apply.invite_invalid', order: 220, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 24,
  },
  'apply.invite_invalid.meta_what_to_do_value': {
    kind: 'prose', seed: 'Ask for a fresh link',
    screen: EXPIRED_SCREEN, label: '“What to do” — the answer',
    group: 'apply.invite_invalid', order: 230, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 48,
  },
  'apply.invite_invalid.headline': {
    kind: 'composed',
    seed: { text: 'This link is no longer valid.', emphasis: [13, 28] },
    screen: EXPIRED_SCREEN, label: 'Headline',
    group: 'apply.invite_invalid', order: 240, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 80,
  },
  'apply.invite_invalid.lede': {
    kind: 'prose',
    seed: 'This link or code may already have been used, or it may have been '
      + 'turned off or replaced with a newer one. '
      + 'Nothing is wrong on your end — it just takes one quick ask to fix.',
    screen: EXPIRED_SCREEN, label: 'Opening paragraph',
    group: 'apply.invite_invalid', order: 250, audience: 'family', max_chars: 320,
    surface: INVITE_STOP_SURFACE,
  },
  'apply.invite_invalid.point_social_worker': {
    kind: 'prose',
    seed: 'Ask your hospital social worker for a fresh TRF application link or code',
    screen: EXPIRED_SCREEN, label: 'What to do next — ask for a fresh link',
    group: 'apply.invite_invalid', order: 260, audience: 'family', max_chars: 120,
    surface: INVITE_STOP_SURFACE,
  },
  'apply.invite_invalid.point_contact_trf': {
    kind: 'linked',
    seed: {
      text: 'Not sure who that is, or don’t have one yet? Reach the Tyler Robinson '
        + 'Foundation team directly at trf.org/get-support — they can help you sort '
        + 'out a fresh link or code',
      link: [98, 117],
      href: 'https://www.trf.org/get-support',
    },
    screen: EXPIRED_SCREEN, label: 'What to do next — get in touch with TRF',
    group: 'apply.invite_invalid', order: 270, audience: 'family', max_chars: 280,
    surface: INVITE_STOP_SURFACE,
  },
  'apply.invite_invalid.point_nothing_lost': {
    kind: 'prose',
    seed: 'A new link opens this same application — nothing you did is lost',
    screen: EXPIRED_SCREEN, label: 'What to do next — nothing they did is lost',
    group: 'apply.invite_invalid', order: 280, audience: 'family', max_chars: 120,
    surface: INVITE_STOP_SURFACE,
  },

  // ── the code-entry box (no-code variant only) ────────────────────────────
  'apply.invite_required.code_entry_eyebrow': {
    kind: 'prose', seed: 'Have an application code?',
    screen: 'Have a code?', label: 'The box’s own heading',
    group: 'apply.invite_required.code_entry', order: 300, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 48,
  },
  'apply.invite_required.code_entry_helper': {
    kind: 'prose',
    seed: 'If your social worker or a TRF team member gave you a code, enter it here.',
    screen: 'Have a code?', label: 'Helper sentence under the heading',
    group: 'apply.invite_required.code_entry', order: 310, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 160,
  },
  'apply.invite_required.code_entry_field_label': {
    kind: 'prose', seed: 'Application code',
    screen: 'Have a code?', label: 'What the code box is called',
    group: 'apply.invite_required.code_entry', order: 320, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 32,
  },
  'apply.invite_required.code_entry_submit': {
    kind: 'prose', seed: 'Continue',
    screen: 'Have a code?', label: 'The button',
    group: 'apply.invite_required.code_entry', order: 330, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 24,
  },
  'apply.invite_required.code_entry_error_invalid': {
    kind: 'prose',
    seed: 'That code isn’t valid — check with the person who gave it to you.',
    screen: 'Have a code?', label: 'What it says when the code is wrong',
    group: 'apply.invite_required.code_entry', order: 340, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 160,
  },
  'apply.invite_required.code_entry_accepted': {
    kind: 'prose',
    seed: 'Code accepted — it will be applied to your application.',
    screen: 'Have a code?', label: 'What it says when the code works',
    group: 'apply.invite_required.code_entry', order: 350, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 120,
  },

  // ── the closed-round screen (#1017) ──────────────────────────────────────
  // Reached only via a ?code= or typed application code whose cycle has sealed.
  // The lede is the DEFAULT: a staff-authored per-cycle `closed_notice`
  // (ApplicationCycle) overrides it when non-empty — every grant product
  // surveyed makes the closed-round message admin-authored, which is why the
  // override exists. This slot is what a family reads when staff wrote nothing.
  'apply.cycle_closed.meta_this_round_key': {
    kind: 'prose', seed: 'This round',
    screen: CLOSED_SCREEN, label: '“This round” — the question',
    group: 'apply.cycle_closed', order: 500, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 32,
  },
  'apply.cycle_closed.meta_this_round_value': {
    kind: 'prose', seed: 'Closed',
    screen: CLOSED_SCREEN, label: '“This round” — the answer',
    group: 'apply.cycle_closed', order: 510, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 48,
  },
  'apply.cycle_closed.meta_what_to_do_key': {
    kind: 'prose', seed: 'What to do',
    screen: CLOSED_SCREEN, label: '“What to do” — the question',
    group: 'apply.cycle_closed', order: 520, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 32,
  },
  'apply.cycle_closed.meta_what_to_do_value': {
    kind: 'prose', seed: 'Wait for the next round',
    screen: CLOSED_SCREEN, label: '“What to do” — the answer',
    group: 'apply.cycle_closed', order: 530, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 48,
  },
  'apply.cycle_closed.headline': {
    kind: 'prose', seed: 'This year’s applications are closed.',
    screen: CLOSED_SCREEN, label: 'Headline',
    group: 'apply.cycle_closed', order: 540, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 80,
  },
  'apply.cycle_closed.lede': {
    kind: 'prose',
    seed: 'The application window for this cycle has ended. Nothing is wrong with your '
      + 'code — it was for a round that has now closed. Your hospital social worker '
      + 'or the TRF team can tell you when the next one opens.',
    screen: CLOSED_SCREEN, label: 'Opening paragraph — staff can replace it per round',
    group: 'apply.cycle_closed', order: 550, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 320,
    note: 'Also the message under the typed-code field when a code’s round has closed '
      + 'and staff wrote no notice for that cycle.',
  },
  'apply.cycle_closed.point_social_worker': {
    kind: 'prose',
    seed: 'Your hospital social worker or the TRF team can tell you when the next round opens',
    screen: CLOSED_SCREEN, label: 'What to do next — who knows when the next round opens',
    group: 'apply.cycle_closed', order: 560, audience: 'family', max_chars: 120,
    surface: INVITE_STOP_SURFACE,
  },
  'apply.cycle_closed.point_contact_trf': {
    kind: 'linked',
    seed: {
      text: 'Not sure who that is, or don’t have one yet? Reach the Tyler Robinson Foundation team directly at trf.org/get-support — they can tell you when the next round opens',
      link: [98, 117],
      href: 'https://www.trf.org/get-support',
    },
    screen: CLOSED_SCREEN, label: 'What to do next — get in touch with TRF',
    group: 'apply.cycle_closed', order: 570, audience: 'family', max_chars: 280,
    surface: INVITE_STOP_SURFACE,
  },

  // ── renewing families only, for now (#3452) ─────────────────────────────
  // The cycle is open, but until its "New families from" day (Settings →
  // Applications) it takes renewing families only. The meta row beside
  // "New families from" is the date itself, filled in by the screen.
  'apply.new_families_soon.meta_this_round_key': {
    kind: 'prose', seed: 'This round',
    screen: NEW_FAMILIES_SCREEN, label: '“This round” — the question',
    group: 'apply.new_families_soon', order: 500, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 32,
  },
  'apply.new_families_soon.meta_this_round_value': {
    kind: 'prose', seed: 'Renewing families',
    screen: NEW_FAMILIES_SCREEN, label: '“This round” — the answer',
    group: 'apply.new_families_soon', order: 510, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 48,
  },
  'apply.new_families_soon.meta_opens_key': {
    kind: 'prose', seed: 'New families from',
    screen: NEW_FAMILIES_SCREEN, label: '“New families from” — the date follows it',
    group: 'apply.new_families_soon', order: 520, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 32,
  },
  'apply.new_families_soon.headline': {
    kind: 'prose', seed: 'Applications for new families open soon.',
    screen: NEW_FAMILIES_SCREEN, label: 'Headline',
    group: 'apply.new_families_soon', order: 540, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 80,
  },
  'apply.new_families_soon.lede': {
    kind: 'prose',
    seed: 'Right now this round is open to families renewing their TRF grant. New families '
      + 'can start an application from the date shown here. If you are renewing, open the '
      + 'link TRF sent you or enter your code.',
    screen: NEW_FAMILIES_SCREEN, label: 'Opening paragraph',
    group: 'apply.new_families_soon', order: 550, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 320,
  },
  'apply.new_families_soon.point_contact_trf': {
    kind: 'linked',
    seed: {
      text: 'Questions before then? Reach the Tyler Robinson Foundation team at trf.org/get-support',
      link: [67, 86],
      href: 'https://www.trf.org/get-support',
    },
    screen: NEW_FAMILIES_SCREEN, label: 'What to do next — get in touch with TRF',
    group: 'apply.new_families_soon', order: 570, audience: 'family', max_chars: 280,
    surface: INVITE_STOP_SURFACE,
  },

  // ── the mistyped-code screen (#1017) ─────────────────────────────────────
  // The code’s check symbol is wrong, so it was mistyped. Computed offline with
  // no database access, so saying "typo" reveals nothing an attacker could not
  // already compute; every OTHER failure still collapses to the invalid screen.
  'apply.code_typo.meta_that_code_key': {
    kind: 'prose', seed: 'That code',
    screen: TYPO_SCREEN, label: '“That code” — the question',
    group: 'apply.code_typo', order: 600, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 32,
  },
  'apply.code_typo.meta_that_code_value': {
    kind: 'prose', seed: 'Has a typo',
    screen: TYPO_SCREEN, label: '“That code” — the answer',
    group: 'apply.code_typo', order: 610, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 48,
  },
  'apply.code_typo.meta_what_to_do_key': {
    kind: 'prose', seed: 'What to do',
    screen: TYPO_SCREEN, label: '“What to do” — the question',
    group: 'apply.code_typo', order: 620, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 32,
  },
  'apply.code_typo.meta_what_to_do_value': {
    kind: 'prose', seed: 'Check it and try again',
    screen: TYPO_SCREEN, label: '“What to do” — the answer',
    group: 'apply.code_typo', order: 630, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 48,
  },
  'apply.code_typo.headline': {
    kind: 'prose', seed: 'That code has a typo in it.',
    screen: TYPO_SCREEN, label: 'Headline',
    group: 'apply.code_typo', order: 640, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 80,
    note: 'Also the message under the typed-code field when the check symbol fails.',
  },
  'apply.code_typo.lede': {
    kind: 'prose',
    seed: 'The code you entered is one or two characters off — codes are easy to mishear '
      + 'and easy to mistype. Check it against what you were given and try again, or '
      + 'carry on without it and tell us who referred you.',
    screen: TYPO_SCREEN, label: 'Opening paragraph',
    group: 'apply.code_typo', order: 650, audience: 'family',
    surface: INVITE_STOP_SURFACE, max_chars: 320,
  },
  'apply.code_typo.point_social_worker': {
    kind: 'prose',
    seed: 'Ask your hospital social worker or TRF to read the code back to you again',
    screen: TYPO_SCREEN, label: 'What to do next — have the code read back',
    group: 'apply.code_typo', order: 660, audience: 'family', max_chars: 120,
    surface: INVITE_STOP_SURFACE,
  },
  'apply.code_typo.point_contact_trf': {
    kind: 'linked',
    seed: {
      text: 'Not sure who that is, or don’t have one yet? Reach the Tyler Robinson Foundation team directly at trf.org/get-support — they can help you sort out the code',
      link: [98, 117],
      href: 'https://www.trf.org/get-support',
    },
    screen: TYPO_SCREEN, label: 'What to do next — get in touch with TRF',
    group: 'apply.code_typo', order: 670, audience: 'family', max_chars: 280,
    surface: INVITE_STOP_SURFACE,
  },

  // ── the application-status screen ────────────────────────────────────────
  // The screen a family lands on after they submit — and, for most families,
  // the ONLY screen they see for weeks. `APPLICATION_STATUS_DISPLAY` supplies
  // the status chip and is deliberately absent from this registry: it is shared
  // with two staff surfaces, so it is not registrable (D-CS-1's ownership rule).
  'apply.status.eyebrow': {
    kind: 'prose', seed: 'Application received',
    screen: STATUS_SCREEN, label: 'The short line above the headline',
    group: 'apply.status', order: 400, audience: 'family',
    surface: STATUS_SURFACE, max_chars: 48,
  },
  // #2400 follow-up: the line above is what a family reads while TRF is
  // deciding. Once there is an outcome the family may see, or they withdrew,
  // “Application received” sits stale over the outcome headline, so they read
  // this neutral line instead; the status chip already names the outcome.
  'apply.status.eyebrow_decided': {
    kind: 'prose', seed: 'Your application',
    screen: STATUS_SCREEN, label: 'The short line above the headline — after a decision',
    group: 'apply.status', order: 401, audience: 'family',
    surface: STATUS_SURFACE, max_chars: 48,
    note: 'Shown after a decision or a withdrawal, above the outcome headline. Keep it '
      + 'neutral: the same line sits above good news and a decline.',
  },
  'apply.status.headline': {
    kind: 'prose', seed: 'It’s in our hands now.',
    screen: STATUS_SCREEN, label: 'Headline',
    group: 'apply.status', order: 410, audience: 'family',
    surface: STATUS_SURFACE, max_chars: 80,
  },
  // #2400: the headline follows the family-visible stage, like the paragraph
  // under the timeline (#1135). The slot above is what a family reads while
  // TRF is deciding; these three are its ALTERNATIVES once a decision is theirs
  // to know. `withdrawn` has no slot — it reads an `applications` catalog line,
  // as its paragraph does.
  //
  // ⚠ PLACEHOLDER SEEDS, for the reason the outcome paragraphs below are: the
  // screen never said anything different, so there was no wording to lift.
  // They are short, true and meant to be overwritten in the editor.
  'apply.status.headline_approved': {
    kind: 'prose', seed: 'Good news.',
    screen: STATUS_SCREEN, label: 'Headline — approved',
    group: 'apply.status', order: 411, audience: 'family',
    surface: STATUS_SURFACE, max_chars: 80,
    note: 'Approved by Victor 2026-10-16. Seeded so the headline never says TRF is still '
      + 'deciding after it has decided.',
  },
  'apply.status.headline_denied': {
    kind: 'prose', seed: 'Your application has been reviewed.',
    screen: STATUS_SCREEN, label: 'Headline — not approved',
    group: 'apply.status', order: 412, audience: 'family',
    surface: STATUS_SURFACE, max_chars: 80,
    note: 'DECISIONS 2026-10-16: the platform does not communicate a decline or a waitlist, so '
      + 'this names no outcome and carries no reason. TRF tells the family by email. Approved by Victor 2026-10-16.',
  },
  'apply.status.headline_waitlisted': {
    kind: 'prose', seed: 'Your application has been reviewed.',
    screen: STATUS_SCREEN, label: 'Headline — waitlisted',
    group: 'apply.status', order: 413, audience: 'family',
    surface: STATUS_SURFACE, max_chars: 80,
    note: 'DECISIONS 2026-10-16: the platform does not communicate a decline or a waitlist, so '
      + 'this names no outcome and carries no reason. TRF tells the family by email. Approved by Victor 2026-10-16.',
  },
  // #2520: what a family reads while TRF has asked them for something
  // (`needs_more_info`). Before this the waiting pair above showed — “It’s in
  // our hands now … we’ll reach out if we need anything else” — directly above
  // the panel naming what TRF needed. Keyed on the status, not the stage: the
  // timeline still reads “Under review”.
  'apply.status.headline_needs_info': {
    kind: 'prose', seed: 'TRF needs something from you.',
    screen: STATUS_SCREEN, label: 'Headline — TRF asked for more',
    group: 'apply.status', order: 414, audience: 'family',
    surface: STATUS_SURFACE, max_chars: 80,
    note: 'Shown while TRF waits on the family. The panel under the timeline lists '
      + 'what was asked, so the headline only has to say that something was.',
  },
  // E2E 2026-10-13: an ADMITTED family read “Good news.” over a paragraph
  // saying TRF would be in touch, and was never told it was in. Keyed on the
  // status ahead of the stage map, like the needs-info pair; a family renewing
  // through /apply (`renewalMarkOf`) reads its own line, as its welcome email
  // does (Mario, 2026-10-13, option (a)).
  'apply.status.headline_admitted': {
    kind: 'prose', seed: 'You’re in.',
    screen: STATUS_SCREEN, label: 'Headline — admitted',
    group: 'apply.status', order: 415, audience: 'family',
    surface: STATUS_SURFACE, max_chars: 80,
    note: 'Shown once TRF has recorded the family’s acceptance and admitted them. A '
      + 'family renewing through the application reads the renewal headline instead.',
  },
  'apply.status.headline_admitted_renewal': {
    kind: 'prose', seed: 'Welcome back.',
    screen: STATUS_SCREEN, label: 'Headline — admitted, renewing family',
    group: 'apply.status', order: 416, audience: 'family',
    surface: STATUS_SURFACE, max_chars: 80,
    note: 'The admitted headline for a family that applied to renew (year 2 or 3). '
      + 'TRF has known this family for a year; their email says “Welcome back” too.',
  },
  'apply.status.body_needs_info': {
    kind: 'prose',
    seed: 'Your TRF case manager asked about the items listed below. Open your application, '
      + 'update them, and send it again. We’ll keep reviewing it as soon as it’s back.',
    screen: STATUS_SCREEN, label: 'What happens next — TRF asked for more',
    group: 'apply.status.outcome', order: 435, audience: 'family', max_chars: 320,
    surface: STATUS_SURFACE,
    note: 'Sits above the panel that names what TRF asked for and the button that opens '
      + 'the application. Say what the family does, not what TRF is doing.',
  },
  'apply.status.submitted_prefix': {
    kind: 'prose', seed: 'Submitted {{date}}',
    screen: STATUS_SCREEN, label: 'The line that says when they sent it',
    group: 'apply.status', order: 420, audience: 'family',
    surface: STATUS_SURFACE, max_chars: 32,
    note: 'Renders as “Submitted 14 August 2026”. {{date}} is where the real date '
      + 'goes, formatted in the reader’s language — keep it in the line. One slot '
      + 'with a placeholder, not a word before the date: another language '
      + 'may need the date first. An edit without {{date}} is not used.',
  },
  // ── the paragraph under the timeline ─────────────────────────
  // FOUR STRINGS IN ONE PLACE ON THE SCREEN, and a family reads exactly one of
  // them (#1135, found by the PR-C walk, ruled 2026-08-23). It used to be a
  // single unconditional slot, so an approved family was told their application
  // “will be reviewed” directly above the note carrying the decision that had
  // already been made.
  //
  // Grouped apart from the eyebrow and the headline on purpose: these are
  // ALTERNATIVES to one another, and an editor who cannot see the other three
  // while writing one of them will write four paragraphs that do not sound like
  // the same foundation. `withdrawn` deliberately has no slot of its own — the
  // ruling named three — and falls back to the first row here.
  //
  // ⚠ THE THREE OUTCOME SEEDS BELOW ARE PLACEHOLDERS. Every other seed in this
  // file was LIFTED byte-for-byte from wording TRF already had; these three had
  // no original to lift, because the screen never said anything different. They
  // are written to be true, short and replaceable — they ship so no family ever
  // reads an empty paragraph, and they are meant to be overwritten in the editor
  // rather than to become TRF’s voice by default.
  'apply.status.body': {
    kind: 'prose',
    seed: 'A TRF case manager will review your application. We’ll reach out if we need '
      + 'anything else, and we’ll be in touch when a decision is made.',
    screen: STATUS_SCREEN, label: 'What happens next — while TRF is deciding',
    group: 'apply.status.outcome', order: 430, audience: 'family', max_chars: 320,
    surface: STATUS_SURFACE,
    note: 'What a family reads while they are still waiting — submitted or under review. '
      + 'A family TRF has asked for more reads its own paragraph, below.',
  },
  'apply.status.body_approved': {
    kind: 'prose',
    seed: 'Your application has been approved. Someone from TRF will be in touch about what '
      + 'happens next.',
    screen: STATUS_SCREEN, label: 'What happens next — approved',
    group: 'apply.status.outcome', order: 431, audience: 'family', max_chars: 320,
    surface: STATUS_SURFACE,
    note: 'Placeholder wording, seeded so the paragraph is never empty. What an approved '
      + 'family reads here is TRF’s to write.',
  },
  'apply.status.body_denied': {
    kind: 'prose',
    seed: 'TRF has emailed you about your application. If you can’t find the email, please contact the TRF team.',
    screen: STATUS_SCREEN, label: 'What happens next — not approved',
    group: 'apply.status.outcome', order: 432, audience: 'family', max_chars: 320,
    surface: STATUS_SURFACE,
    note: 'DECISIONS 2026-10-16: the platform does not communicate a decline or a waitlist, so '
      + 'this names no outcome and carries no reason. TRF tells the family by email. Approved by Victor 2026-10-16.',
  },
  'apply.status.body_waitlisted': {
    kind: 'prose',
    seed: 'TRF has emailed you about your application. If you can’t find the email, please contact the TRF team.',
    screen: STATUS_SCREEN, label: 'What happens next — waitlisted',
    group: 'apply.status.outcome', order: 433, audience: 'family', max_chars: 320,
    surface: STATUS_SURFACE,
    note: 'DECISIONS 2026-10-16: the platform does not communicate a decline or a waitlist, so '
      + 'this names no outcome and carries no reason. TRF tells the family by email. Approved by Victor 2026-10-16.',
  },
  // ── from approval to admission (E2E 2026-10-13) ──────────────────────────
  // `body_approved` above was the one paragraph for every state from the
  // welcome email to admission, so an admitted family still read that TRF
  // would be in touch. Each step now says what is true at that step, keyed on
  // the STATUS (the stage is `approved` for all of them). `body_approved` stays
  // for the offer states DocuSign will bring (declined, voided) and legacy rows.
  // No line promises a letter or a signature: until DocuSign (#1043) there is
  // none, and TRF records the acceptance by hand (#2735).
  'apply.status.body_offer_sent': {
    kind: 'prose',
    seed: 'Your application has been approved. Someone from TRF will contact you to confirm '
      + 'that you accept. Nothing else is needed from you until then.',
    screen: STATUS_SCREEN, label: 'What happens next — approved, welcome email sent',
    group: 'apply.status.outcome', order: 436, audience: 'family', max_chars: 320,
    surface: STATUS_SURFACE,
    note: 'Shown after TRF sends the welcome email and before it records that the family '
      + 'accepts. Staff record the acceptance by hand when the family confirms it. Do not '
      + 'mention a letter or a signature: there is none yet.',
  },
  'apply.status.body_offer_sent_renewal': {
    kind: 'prose',
    seed: 'Your renewal has been approved. Someone from TRF will contact you to confirm '
      + 'that you accept. Nothing else is needed from you until then.',
    screen: STATUS_SCREEN, label: 'What happens next — renewal approved, welcome email sent',
    group: 'apply.status.outcome', order: 437, audience: 'family', max_chars: 320,
    surface: STATUS_SURFACE,
    note: 'The same moment for a family that applied to renew (year 2 or 3). Write it with '
      + 'the paragraph above.',
  },
  'apply.status.body_acceptance_recorded': {
    kind: 'prose',
    seed: 'TRF has recorded that you accept. We’re setting up your family’s account and '
      + 'your grant now.',
    screen: STATUS_SCREEN, label: 'What happens next — acceptance recorded',
    group: 'apply.status.outcome', order: 438, audience: 'family', max_chars: 320,
    surface: STATUS_SURFACE,
    note: 'Usually seen for a moment only: admission follows the recorded acceptance at '
      + 'once. A family stays here if admission needs a retry by staff.',
  },
  'apply.status.body_admitted': {
    kind: 'prose',
    seed: 'Welcome to TRF. Your family’s account is ready, and your grant is on your '
      + 'Grants page.',
    screen: STATUS_SCREEN, label: 'What happens next — admitted',
    group: 'apply.status.outcome', order: 439, audience: 'family', max_chars: 320,
    surface: STATUS_SURFACE,
    note: 'Admission creates the family’s Signature grant, so it is already on the Grants '
      + 'page. The button under this paragraph opens that page.',
  },
  'apply.status.body_admitted_renewal': {
    kind: 'prose',
    seed: 'Your renewal is approved. Your family’s account is ready, and your grant is on '
      + 'your Grants page.',
    screen: STATUS_SCREEN, label: 'What happens next — renewal admitted',
    group: 'apply.status.outcome', order: 440, audience: 'family', max_chars: 320,
    surface: STATUS_SURFACE,
    note: 'The admitted paragraph for a family that applied to renew (year 2 or 3).',
  },
  'apply.status.grants_cta': {
    kind: 'prose', seed: 'See your grant',
    screen: STATUS_SCREEN, label: 'The button to the Grants page — admitted',
    group: 'apply.status.outcome', order: 441, audience: 'family', max_chars: 32,
    surface: STATUS_SURFACE,
  },

  // ── the progress timeline ────────────────────────────────────────────────
  // Three labels living in a module-level const array — the shape a JSX-text
  // scan cannot see, which is why the coverage guard reads const objects too.
  'apply.status.timeline_submitted': {
    kind: 'prose', seed: 'Submitted',
    screen: TIMELINE_SCREEN, label: 'First step’s name',
    group: 'apply.status.timeline', order: 440, audience: 'family',
    surface: STATUS_SURFACE, max_chars: 24,
  },
  'apply.status.timeline_review': {
    kind: 'prose', seed: 'Under review',
    screen: TIMELINE_SCREEN, label: 'Second step’s name',
    group: 'apply.status.timeline', order: 450, audience: 'family',
    surface: STATUS_SURFACE, max_chars: 24,
  },
  'apply.status.timeline_decision': {
    kind: 'prose', seed: 'Decision',
    screen: TIMELINE_SCREEN, label: 'Third step’s name',
    group: 'apply.status.timeline', order: 460, audience: 'family',
    surface: STATUS_SURFACE, max_chars: 24,
  },

  // ── the "we need more from you" panel ────────────────────────────────────
  // Shown only while the application is `needs_more_info`. The flagged-field
  // list beneath it is NOT registrable: it is data, each field read in the
  // form's own words (`useFlaggedFieldLabels`, #2520), not a copy slot.
  'apply.status.needs_info_label': {
    kind: 'prose', seed: 'We need a bit more from you',
    screen: NEEDS_INFO_SCREEN, label: 'What the panel is called',
    group: 'apply.status.needs_info', order: 470, audience: 'family',
    surface: STATUS_SURFACE, max_chars: 48,
  },
  'apply.status.needs_info_cta': {
    kind: 'prose', seed: 'Continue your application',
    screen: NEEDS_INFO_SCREEN, label: 'The button',
    group: 'apply.status.needs_info', order: 480, audience: 'family',
    surface: STATUS_SURFACE, max_chars: 32,
  },
  // #1006 — the two strings a document ask adds to this panel. The KIND is
  // not registrable (it is a vocabulary pinned to the server, not copy) and
  // the REASON is not copy at all: the case manager types it per family and it
  // is rendered verbatim (D-SE-17). These two are the wrapper around it.
  'apply.status.documents_attribution': {
    kind: 'prose', seed: 'From your TRF case manager:',
    screen: NEEDS_INFO_SCREEN, label: 'Words before the case manager’s reason',
    group: 'apply.status.needs_info', order: 481, audience: 'family',
    surface: STATUS_SURFACE, max_chars: 64,
  },
  'apply.status.documents_reassurance': {
    kind: 'prose', seed: 'Everything else you sent is saved — you only need to add what’s listed above.',
    screen: NEEDS_INFO_SCREEN, label: 'The line that says nothing else has to be redone',
    group: 'apply.status.needs_info', order: 482, audience: 'family',
    surface: STATUS_SURFACE, max_chars: 160,
  },

  // ── the decision note ────────────────────────────────────────────────────
  'apply.status.decision_note_label': {
    kind: 'prose', seed: 'Note from TRF',
    screen: DECISION_SCREEN, label: 'What the panel is called',
    group: 'apply.status.decision', order: 490, audience: 'family',
    surface: STATUS_SURFACE, max_chars: 48,
    note: 'The note itself is written per family by the reviewing case manager '
      + '(`decision_reason`) — data, not copy. Only this label is editable.',
  },

  // ── a document TRF attached for the family (#1005 / AP-22) ───────────────
  'apply.documents.added_by_trf': {
    kind: 'prose', seed: 'TRF added this for you',
    screen: 'Supporting documents',
    label: 'Marker on a document TRF uploaded for them',
    group: 'apply.documents', order: 510, audience: 'family',
    surface: 'components/apply/DocumentUploadStep.tsx', max_chars: 48,
    note: 'Staff can attach a document a family sent by post or read out by phone '
      + '(#1005). This is what the FAMILY reads beside it — prose about something '
      + 'they did not do, where the line between reassuring and accusatory is a '
      + 'comms judgement, not an engineering one. The seed is a draft.',
  },

  // ── too many files chosen for a multi-file slot (#1327) ─────────────────
  'apply.documents.cap_toast_title': {
    kind: 'prose', seed: 'Some files were not added',
    screen: 'Supporting documents',
    label: 'What it says when they pick too many files',
    group: 'apply.documents', order: 520, audience: 'family',
    surface: 'components/apply/DocumentUploadStep.tsx', max_chars: 48,
    note: 'The body of the notice names the slot, the room left and the files that '
      + 'were not added — data, interpolated in source, exempt in copy-coverage. '
      + 'Only this title is editable. The seed is a draft.',
  },

  // ── the four document-tile hints (#1009) ────────────────────────────────
  // Only the HINTS are registered. The tile labels come from
  // `DOCUMENT_KIND_LABELS` (lib/documents.ts), which staff surfaces render
  // too, so that map is shared staff/family and not registrable — D-CS-1's
  // ownership rule, the same reasoning as `APPLICATION_STATUS_DISPLAY` above.
  // Resolved in ApplicationWizard, which builds the DocSlot list and hands it
  // to DocumentUploadStep as props.
  'apply.documents.hint_diagnosis_letter': {
    kind: 'prose', seed: 'From your care team',
    screen: 'Supporting documents',
    label: 'Hint under the diagnosis letter',
    group: 'apply.documents', order: 530, audience: 'family',
    surface: 'pages/apply/ApplicationWizard.tsx', max_chars: 64,
  },
  'apply.documents.hint_income_verification': {
    kind: 'prose', seed: 'A recent paystub',
    screen: 'Supporting documents',
    label: 'Hint under the income verification',
    group: 'apply.documents', order: 540, audience: 'family',
    surface: 'pages/apply/ApplicationWizard.tsx', max_chars: 64,
  },
  'apply.documents.hint_bill_statement': {
    kind: 'prose', seed: 'Recent bills — rent, utilities, medical or other — up to 5',
    screen: 'Supporting documents',
    label: 'Hint under the bill statement',
    group: 'apply.documents', order: 550, audience: 'family',
    surface: 'pages/apply/ApplicationWizard.tsx', max_chars: 64,
    note: 'The "up to 5" restates DOCUMENT_KIND_SLOT_CAPS.bill_statement — the '
      + 'cap itself is code, so an edit here changes the sentence, never the '
      + 'limit. At the cap the tile swaps this for its own "Maximum N reached" '
      + 'line, whose count is data.',
  },
  // #2472 (D10): the child's photo is its own slot, beside the family photo.
  'apply.documents.hint_child_photo': {
    kind: 'prose', seed: 'A recent photo of your child — add up to 3',
    screen: 'Supporting documents',
    label: 'Hint under the photo of the child',
    group: 'apply.documents', order: 555, audience: 'family',
    surface: 'pages/apply/ApplicationWizard.tsx', max_chars: 64,
    note: 'The "up to 3" restates DOCUMENT_KIND_SLOT_CAPS.child_photo — the cap '
      + 'itself is code, so an edit here changes the sentence, never the limit.',
  },
  'apply.documents.hint_family_photo': {
    kind: 'prose', seed: 'Your child with family or siblings — add up to 3',
    screen: 'Supporting documents',
    label: 'Hint under the family photo',
    group: 'apply.documents', order: 560, audience: 'family',
    surface: 'pages/apply/ApplicationWizard.tsx', max_chars: 64,
    note: 'This tile used to hold both photos ("one photo of your child and '
      + 'one of your family — add up to 5"); the child’s photo has its own slot '
      + 'now. The "up to 3" restates DOCUMENT_KIND_SLOT_CAPS.family_photo.',
  },

  // ── the read-only answers summary on the final step (#1114) ─────────────
  // The QUESTIONS and their SECTION headings on this screen come from the
  // stamped form definition and are edited in the form builder — D-CS-1's
  // ownership rule keeps them out of here. Only the screen's own five
  // chrome strings are the copy store's.
  'apply.review.heading': {
    kind: 'prose', seed: 'Check your answers',
    screen: 'Review answers',
    label: 'Heading above their own answers',
    group: 'apply.review', order: 700, audience: 'family',
    surface: 'components/apply/ReviewSummary.tsx', max_chars: 48,
    note: 'TRF (2026-08-29) decided a family reads its answers back before it '
      + 'consents and submits. This heads that block.',
  },
  // #1114 — the review step's intro line.
  'apply.review.intro': {
    kind: 'prose',
    seed: 'This is everything you told us. Read it over before you agree and submit.',
    screen: 'Review answers',
    label: 'Line telling them what this block is for',
    group: 'apply.review', order: 710, audience: 'family', max_chars: 160,
    surface: 'components/apply/ReviewSummary.tsx',
    note: 'Deliberately does NOT tell the family to "use Back": Back moves one '
      + 'step, so changing an answer eleven screens up costs eleven presses and '
      + 'ten forward gate-passes. Naming it would promise a one-click fix this '
      + 'step does not have. If jump-to-step navigation is ever built, this '
      + 'sentence is where it gets announced.',
  },
  'apply.review.retired_heading': {
    kind: 'prose', seed: 'Questions we no longer ask',
    screen: 'Review answers',
    label: 'Heading over questions TRF no longer asks',
    group: 'apply.review', order: 730, audience: 'family', max_chars: 48,
    surface: 'components/apply/ReviewSummary.tsx',
    note: 'Only appears for a family that answered a question TRF later '
      + 'retired: the tail exists so its own answer never vanishes from a block '
      + 'that says "everything you told us". Like the grant heading, it names a '
      + 'block the form store does not own, so it lives here.',
  },
  // The family-facing half of the retired-answer explanation; the staff
  // wording is EditableFieldRow's RETIRED_NOTE, per DECISIONS D-SE-13.
  'apply.review.retired_note': {
    kind: 'prose',
    seed: 'You answered these earlier. We no longer ask them, and your answers stay on file.',
    screen: 'Review answers',
    label: 'Why retired questions still show with their answers',
    group: 'apply.review', order: 740, audience: 'family', max_chars: 160,
    surface: 'components/apply/ReviewSummary.tsx',
    note: 'Without it the heading reads as an accusation of answering something '
      + 'that was never asked. Staff see the same fact worded for them on the '
      + 'editable field row; this is the family half.',
  },

  // ── the wizard's code-pinned consent steps (#1009) ──────────────────────
  // Only the CHROME is here. The explanation and the sentence a family ticks
  // are excluded by AP-26 itself: that wording belongs to the versioned legal
  // documents, never to a copy edit. The PHI/PIPEDA sentences are additionally
  // unreachable — `findLegalPhrase` (copyLocks.js) refuses their phrases
  // store-wide; the publicity pair carries no barred phrase and stays out by
  // not being registered.
  'apply.wizard.phi_gate.eyebrow': {
    kind: 'prose', seed: 'Before we ask about care',
    screen: 'Health consent',
    label: 'The short line above the headline',
    group: 'apply.wizard', order: 800, audience: 'family',
    surface: 'pages/apply/ApplicationWizard.tsx', max_chars: 48,
    // The CA reframing rule is #329/D-7 — provenance lives here, not in the
    // staff-facing note (provenance-guard.test.ts).
    note: 'Shared by the CA (PIPEDA) variant of the same step, which reframes '
      + 'only the legal sentences.',
  },
  'apply.wizard.phi_gate.title': {
    kind: 'prose', seed: 'About your child’s health',
    screen: 'Health consent',
    label: 'Headline',
    group: 'apply.wizard', order: 810, audience: 'family',
    surface: 'pages/apply/ApplicationWizard.tsx', max_chars: 80,
    // Suffix pinning is #329/D-7 — provenance in comment, not in the note.
    note: 'The CA (PIPEDA) variant renders this same slot with a code-pinned '
      + '" (Canada)" suffix, so an edit follows both branches and neither can '
      + 'drift.',
  },
  'apply.wizard.publicity.eyebrow': {
    kind: 'prose', seed: 'Optional',
    screen: 'Photos and stories',
    label: 'The short line above the headline',
    group: 'apply.wizard', order: 820, audience: 'family',
    surface: 'pages/apply/ApplicationWizard.tsx', max_chars: 48,
  },
  'apply.wizard.publicity.title': {
    kind: 'prose', seed: 'Photos and stories',
    screen: 'Photos and stories',
    label: 'Headline — also the step’s name in the progress bar',
    group: 'apply.wizard', order: 830, audience: 'family',
    surface: 'pages/apply/ApplicationWizard.tsx', max_chars: 80,
    note: 'The progress bar has always named this step with the same words as '
      + 'its headline; one slot serves both so they cannot drift apart.',
  },

  // ── the health-information step, while its region is unknown (#1636) ─────
  //
  // The CA branch of the consent step asks the server which document a Canadian
  // family should be shown. When that request FAILS, the step has to say so
  // instead of guessing — a wrong guess showed a Canadian family the US HIPAA
  // release and wrote an immutable ConsentEvent against it.
  //
  // These are registrable precisely BECAUSE they are not consent copy, which is
  // the distinction the whole surface turns on. The consent sentences two blocks
  // up are barred store-wide by `findLegalPhrase` (copyLocks.js). These say only
  // that a page did not load and offer another attempt; they neither restate nor
  // stand in for the document, so an edit here can never change what a family
  // agreed to. `findLegalPhrase` keeps that true under editing rather than by
  // convention — it refuses "PIPEDA", "I authorize" and their kin in any copy
  // value, so this slot cannot be turned into a consent gate by a copy edit.
  'apply.wizard.region_step.loading_eyebrow': {
    kind: 'prose', seed: 'Just a moment',
    screen: 'Health consent, loading',
    label: 'The short line above the loading message',
    group: 'apply.wizard', order: 840, audience: 'family', max_chars: 48,
    surface: 'pages/apply/ApplicationWizard.tsx',
  },
  'apply.wizard.region_step.loading_body': {
    kind: 'prose', seed: 'Loading this step for your region.',
    screen: 'Health consent, loading',
    label: 'What it says while the step loads',
    group: 'apply.wizard', order: 850, audience: 'family', max_chars: 80,
    surface: 'pages/apply/ApplicationWizard.tsx',
    note: 'Shown only to a family outside the US, and only until the answer '
      + 'lands. The step deliberately shows nothing to sign while it waits.',
  },
  'apply.wizard.region_step.error_eyebrow': {
    kind: 'prose', seed: 'This step didn’t load',
    screen: 'Health consent, failed to load',
    label: 'The short line above the headline',
    group: 'apply.wizard', order: 860, audience: 'family', max_chars: 48,
    surface: 'pages/apply/ApplicationWizard.tsx',
  },
  'apply.wizard.region_step.error_title': {
    kind: 'prose', seed: 'We couldn’t load this step',
    screen: 'Health consent, failed to load',
    label: 'Headline — also the title of the pop-up',
    group: 'apply.wizard', order: 870, audience: 'family', max_chars: 80,
    surface: 'pages/apply/ApplicationWizard.tsx',
    note: 'One slot serves the headline on the page and the pop-up message, so '
      + 'the two cannot drift apart.',
  },
  'apply.wizard.region_step.error_body': {
    kind: 'prose',
    seed: 'We weren’t able to reach TRF to load the right version of this step for your '
      + 'region, so we haven’t shown you one. Nothing has been recorded, and every answer '
      + 'you’ve given is saved. Please check your connection and try again — if it keeps '
      + 'failing, contact TRF and we’ll take it from there.',
    screen: 'Health consent, failed to load',
    label: 'The explanation under the headline',
    group: 'apply.wizard', order: 880, audience: 'family', max_chars: 320,
    surface: 'pages/apply/ApplicationWizard.tsx',
    note: 'Says what did not happen and what is safe — never what the step '
      + 'would have asked. A step nobody could load has nothing to say about '
      + 'what it asks.',
  },
  'apply.wizard.region_step.error_retry': {
    kind: 'prose', seed: 'Try again',
    screen: 'Health consent, failed to load',
    label: 'The button that asks TRF for the step again',
    group: 'apply.wizard', order: 890, audience: 'family', max_chars: 24,
    surface: 'pages/apply/ApplicationWizard.tsx',
    note: 'The only NEW affordance on that surface. The way back off the step '
      + 'is the wizard’s own "Back", which every consent step already '
      + 'renders — a second one here put two retreats on one screen.',
  },
  'apply.wizard.region_step.error_toast_body': {
    kind: 'prose',
    seed: 'Your answers are saved. Check your connection and try again — nothing '
      + 'has been recorded for this step.',
    screen: 'Health consent, failed to load',
    label: 'What the pop-up says',
    group: 'apply.wizard', order: 910, audience: 'family', max_chars: 160,
    surface: 'pages/apply/ApplicationWizard.tsx',
  },

  // ── the definition-unavailable panel (#1009) ─────────────────────────────
  'apply.definition_gate.unavailable_body': {
    kind: 'prose',
    seed: 'Nothing you’ve entered has been lost. Please try again — if it keeps '
      + 'happening, get in touch and we’ll help you finish by another route.',
    screen: 'Questions didn’t load',
    label: 'Reassurance under the error',
    group: 'apply.definition_gate', order: 900, audience: 'family',
    surface: 'components/apply/FormDefinitionGate.tsx', max_chars: 320,
    note: 'Renders on BOTH definition-driven surfaces — the application wizard '
      + 'and the pre-screen — one panel, one slot. The line above it names what '
      + 'failed to load and is interpolated, so only this sentence is editable.',
  },

  // ── the renewal confirmation set (#1585 / B-573) ─────────────────────────
  // The renewal IS the Year 2+ application: it runs through ApplicationWizard
  // (flow === 'renewal'), lands as a FamilyApplication, and submit navigates
  // straight to the grants tab — the renewal card there IS the confirmation a
  // family reads; there is no success toast. Namespaced `apply.renewal`, not
  // `grants.*`, for exactly that reason: these strings are the shell around the
  // renewal application form, so Mario's 2026-08-29 move-out condition ("every
  // slot is apply.*") stays true — the store has not outgrown the application
  // flow, this is more of it.
  //
  // The two STATUS lines seed from lib/renewal-display.ts — the single source
  // the card, the seasonal Home banner and the wizard callout all derive from
  // (08-19 call ruling). The lib stays pure and keeps its literals as the seed
  // defaults; the component layer resolves through useCopy() and passes the
  // resolver in, so the slots are registered ONCE, not once per surface.
  'apply.renewal.status_submitted': {
    kind: 'prose',
    seed: 'Submitted — your TRF team is reviewing it',
    screen: RENEWAL_CARD_SCREEN,
    label: 'Status line once the renewal is sent',
    group: 'apply.renewal.status', order: 1000, audience: 'family',
    surface: 'lib/renewal-display.ts', max_chars: 80,
    note: 'The confirmation a family reads after submitting a renewal — the '
      + 'grants tab is the confirmation screen; nothing else announces it.',
  },
  'apply.renewal.status_under_review': {
    kind: 'prose',
    seed: 'Under review',
    screen: RENEWAL_CARD_SCREEN,
    label: 'Status line while TRF reviews it',
    group: 'apply.renewal.status', order: 1010, audience: 'family',
    surface: 'lib/renewal-display.ts', max_chars: 48,
  },
  'apply.renewal.card_heading': {
    kind: 'prose',
    seed: 'Renew your Signature Grant',
    screen: RENEWAL_CARD_SCREEN,
    label: 'The card’s heading',
    group: 'apply.renewal', order: 1020, audience: 'family',
    surface: 'components/grants/RenewalCard.tsx', max_chars: 48,
    note: 'The Year 2 / Year 3 tag beside it comes from the renewal record — '
      + 'data, not part of the slot.',
  },
  'apply.renewal.request_title': {
    kind: 'prose',
    seed: 'Renewal request',
    screen: RENEWAL_CARD_SCREEN,
    label: 'Naming the request — before its year is known',
    group: 'apply.renewal', order: 1030, audience: 'family',
    surface: 'components/grants/RenewalCard.tsx', max_chars: 32,
  },
  'apply.renewal.request_title_with_year': {
    kind: 'prose',
    seed: '{{year}} renewal request',
    screen: RENEWAL_CARD_SCREEN,
    label: 'Naming the request — once its year is known',
    group: 'apply.renewal', order: 1040, audience: 'family',
    surface: 'components/grants/RenewalCard.tsx', max_chars: 32,
    note: 'Renders as “Year 2 renewal request”. {{year}} is where the renewal '
      + 'year goes — keep it in the line. One slot with a placeholder, not the '
      + 'words after the year: another language may need the year last. An '
      + 'edit without {{year}} is not used.',
  },
  'apply.renewal.error_line': {
    kind: 'prose',
    seed: 'Couldn’t load your renewal status. Please refresh to try again.',
    screen: RENEWAL_CARD_SCREEN,
    label: 'What it says when the status won’t load',
    group: 'apply.renewal', order: 1050, audience: 'family',
    surface: 'components/grants/RenewalCard.tsx', max_chars: 160,
  },
  'apply.renewal.invite_line': {
    kind: 'prose',
    seed: 'Year 2+ families can request a Signature Grant renewal. We’ll pre-fill what we already know.',
    screen: RENEWAL_CARD_SCREEN,
    label: 'The invitation, before a renewal exists',
    group: 'apply.renewal', order: 1060, audience: 'family',
    surface: 'components/grants/RenewalCard.tsx', max_chars: 200,
  },

} as const satisfies Record<string, CopySlot>;

export type CopySlotKey = keyof typeof COPY_SLOTS;

/**
 * Deliberately NOT registered, and each for a reason worth writing down:
 *
 * - `frontend/src/lib/applications-display.ts` — the application status labels.
 *   They look family-facing, and they are, but ApplicationDecisionDialog and
 *   ApplicationsQueuePage render the same map to STAFF. A shared staff/family
 *   map is not registrable (D-CS-1's ownership rule): a comms edit intended for
 *   one screen would silently change two internal ones. ApplicationsQueuePage
 *   also lowercases the label mid-sentence, so an edit with internal
 *   capitalisation would render wrong there and nowhere else.
 * - `ApplicationStatusPage`'s `flagged_fields` list — data, not copy. Each
 *   field reads in the form's own words (`useFlaggedFieldLabels`, #2520); a
 *   registry cannot reach a value that is data.
 * - The application-code placeholder mask `TRF-XXXX-XXXX` — the backend
 *   normaliser is coupled to its shape, so changing it is a code change.
 * - The PHI/PIPEDA consent blocks, the publicity release and the data-sharing
 *   gate — legal-bearing, excluded by AP-26 itself.
 */
export const NOT_REGISTRABLE = Object.freeze([
  'frontend/src/lib/applications-display.ts',
  'frontend/src/lib/documents.ts',
]);
